// src/lib/consent.ts
// Single source of truth for the visitor's cookie/tracking choice.
//
// This started life inside <CookieConsent />, but the choice now gates more than
// Google Analytics: any third party we embed that tracks on load has to respect
// it too (see <MailingListForm />, which iframes a Square-hosted page that fires
// Square's own analytics pixel the moment it mounts). Anything added here that
// loads a tracking third party must read hasTrackingConsent() first.
//
// The store is reactive so an Accept click takes effect without a page reload,
// and it syncs across tabs via the storage event.

import { useSyncExternalStore } from "react";

const STORAGE_KEY = "cookie-consent";

export type ConsentChoice = "accepted" | "declined";

const listeners = new Set<() => void>();

const notify = (): void => listeners.forEach((l) => l());

/**
 * The browser's Global Privacy Control signal. When present it is a legally
 * recognized opt-out (CCPA/CPRA and most 2024–26 state privacy laws) and
 * overrides any stored choice — the browser has already spoken for the user.
 */
export const isGpcEnabled = (): boolean =>
  (navigator as Navigator & { globalPrivacyControl?: boolean })
    .globalPrivacyControl === true;

/** The raw stored choice, ignoring GPC. Use this to decide whether to prompt. */
export const readConsent = (): ConsentChoice | null => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "accepted" || value === "declined" ? value : null;
  } catch {
    return null;
  }
};

export const writeConsent = (value: ConsentChoice): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // localStorage unavailable (private mode/quota) — the banner just re-shows
    // next visit and nothing is loaded, which is the privacy-safe default.
  }
  notify();
};

export const clearConsent = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
  notify();
};

/**
 * Whether we may load tracking third parties. The default is false: no stored
 * choice means no consent yet, and GPC always wins.
 */
export const hasTrackingConsent = (): boolean =>
  !isGpcEnabled() && readConsent() === "accepted";

export const subscribeConsent = (listener: () => void): (() => void) => {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
};

/** Reactive hasTrackingConsent() for components that gate on the choice. */
export const useTrackingConsent = (): boolean =>
  useSyncExternalStore(
    subscribeConsent,
    hasTrackingConsent,
    () => false, // server/prerender: never assume consent
  );
